import React, { Component } from "react";
import { MqttContext } from "../../Contexts/MqttProvider";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Grid,
  Skeleton,
  Divider,
  Chip,
} from "@mui/material";
import {
  AccountBalanceWallet,
  CurrencyBitcoin,
  EuroSymbol,
  MonetizationOn,
  ShowChart,
  TrendingDown,
  TrendingUp,
} from "@mui/icons-material";
import { apiAccountMetricPairTypes } from "../../Service/api";

const TRADE_SYMBOL_OVERRIDES = {
  117117224: "EURUSD",
  270886869: "XAUUSDm",
  270638630: "XAUUSDm",
  206985092: "XAUUSDm",
};

const BASE_CONFIG = {
  ALL: {
    label: "All",
    Icon: ShowChart,
    color: "#285b89",
    bg: "#edf2f8",
    border: "#d7e0ec",
  },
  EUR: {
    label: "EUR",
    Icon: EuroSymbol,
    color: "#1769aa",
    bg: "#e3f2fd",
    border: "#bbdefb",
  },
  XAU: {
    label: "XAU",
    Icon: MonetizationOn,
    color: "#946200",
    bg: "#fff4d6",
    border: "#f8dda0",
  },
  BTC: {
    label: "BTC",
    Icon: CurrencyBitcoin,
    color: "#a14f00",
    bg: "#fff1df",
    border: "#ffd4a6",
  },
  GBPJPY: {
    label: "GBPJPY",
    Icon: ShowChart,
    color: "#05745e",
    bg: "#e1f7f0",
    border: "#afe4d5",
  },
  OTHER: {
    label: "Other",
    Icon: AccountBalanceWallet,
    color: "#475569",
    bg: "#f1f5f9",
    border: "#dbe4ef",
  },
};

const ICON_MAP = {
  AccountBalanceWallet,
  CurrencyBitcoin,
  EuroSymbol,
  MonetizationOn,
  ShowChart,
};

const RESPONSIVE_TEXT = {
  baseTab: { xs: "0.7rem", sm: "0.76rem", md: "0.82rem", lg: "0.88rem", xl: "0.94rem" },
  pairTab: { xs: "0.68rem", sm: "0.74rem", md: "0.8rem", lg: "0.86rem", xl: "0.92rem" },
  title: { xs: "0.84rem", sm: "0.92rem", md: "1rem", lg: "1.08rem", xl: "1.16rem" },
  meta: { xs: "0.58rem", sm: "0.64rem", md: "0.68rem", lg: "0.72rem", xl: "0.76rem" },
  label: { xs: "0.6rem", sm: "0.65rem", md: "0.7rem", lg: "0.74rem", xl: "0.78rem" },
  value: { xs: "0.82rem", sm: "0.88rem", md: "0.96rem", lg: "1.04rem", xl: "1.12rem" },
  detailTitle: { xs: "0.74rem", sm: "0.8rem", md: "0.86rem", lg: "0.92rem", xl: "0.98rem" },
  detailValue: { xs: "0.72rem", sm: "0.78rem", md: "0.84rem", lg: "0.9rem", xl: "0.96rem" },
  chip: { xs: "0.62rem", sm: "0.66rem", md: "0.7rem", lg: "0.74rem", xl: "0.78rem" },
  price: { xs: "0.7rem", sm: "0.76rem", md: "0.82rem", lg: "0.88rem", xl: "0.94rem" },
  icon: { xs: 13, sm: 14, md: 15, lg: 16, xl: 17 },
};

class AccountMetricTabs extends Component {
  static contextType = MqttContext;

  constructor(props) {
    super(props);
    this.state = {
      activeAccountNumber: "",
      activeBase: "",
      pairTypes: [],
      isLoadingPairTypes: true,
      pairTypesError: null,
    };
    this.subscribedMetricAccounts = new Set();
    this.subscribedTradeTopics = new Set();
    this.metricSubscriptionSignature = "";
    this.tradeSubscriptionSignature = "";
    this.isMountedComponent = false;
  }

  componentDidMount() {
    this.isMountedComponent = true;
    this.fetchPairTypes();
    this.syncActiveMetricSubscription();
  }

  componentDidUpdate() {
    this.syncActiveMetricSubscription();
  }

  componentWillUnmount() {
    this.isMountedComponent = false;
    this.unsubscribeMetricAccounts([...this.subscribedMetricAccounts]);
    this.unsubscribeTradeTopics([...this.subscribedTradeTopics]);
  }

  fetchPairTypes = async () => {
    try {
      const response = await apiAccountMetricPairTypes();
      const records =
        response &&
        response.data &&
        response.data.status === "OK" &&
        Array.isArray(response.data.records)
          ? response.data.records
          : [];

      const pairTypes = records
        .map((item) => ({
          accountNumber: String(item.account_number || item.account_master || ""),
          key: item.key || "",
          label: item.label || item.key || item.account_master || "",
          base: item.base || "",
          capital:
            item.capital !== null && item.capital !== undefined
              ? Number(item.capital)
              : null,
          currency: item.currency || "",
          isCent:
            item.is_cent === true ||
            String(item.currency || "").toLowerCase() === "cents",
          tradeSymbol: item.trade_symbol || "",
          iconName: item.icon_name || "",
          bgColor: item.bg_color || "",
          textColor: item.text_color || "",
          borderColor: item.border_color || "",
          iconColor: item.icon_color || "",
        }))
        .filter((item) => item.accountNumber);

      if (!this.isMountedComponent) {
        return;
      }

      this.setState((prevState) => {
        const activeStillExists = pairTypes.some(
          (item) => item.accountNumber === prevState.activeAccountNumber,
        );
        const nextActiveAccountNumber = activeStillExists
          ? prevState.activeAccountNumber
          : (pairTypes[0] && pairTypes[0].accountNumber) || "";
        const nextActivePairType = pairTypes.find(
          (item) => item.accountNumber === nextActiveAccountNumber,
        );
        const nextActiveBase =
          prevState.activeBase || this.getBaseName(nextActivePairType);

        return {
          pairTypes,
          activeAccountNumber: nextActiveAccountNumber,
          activeBase: nextActiveBase || "ALL",
          isLoadingPairTypes: false,
          pairTypesError:
            pairTypes.length > 0 ? null : "Pair type data belum tersedia.",
        };
      });
    } catch (error) {
      if (!this.isMountedComponent) {
        return;
      }

      this.setState({
        pairTypes: [],
        activeAccountNumber: "",
        activeBase: "",
        isLoadingPairTypes: false,
        pairTypesError: "Gagal mengambil pair type dari API.",
      });
    }
  };

  subscribeMetricAccount = (accountNumber) => {
    const { subscribeToTopic, client } = this.context;
    if (!accountNumber || !client || !subscribeToTopic) {
      return;
    }

    subscribeToTopic(`Account/${accountNumber}/metricOntick`);
    subscribeToTopic(`Account/${accountNumber}/profit/utcp0`);
    this.subscribedMetricAccounts.add(accountNumber);
  };

  unsubscribeMetricAccount = (accountNumber) => {
    const { unsubscribeFromTopic } = this.context;
    if (!accountNumber || !unsubscribeFromTopic) {
      return;
    }

    unsubscribeFromTopic(`Account/${accountNumber}/metricOntick`);
    unsubscribeFromTopic(`Account/${accountNumber}/profit/utcp0`);
    this.subscribedMetricAccounts.delete(accountNumber);
  };

  unsubscribeMetricAccounts = (accountNumbers) => {
    accountNumbers.forEach((accountNumber) => {
      this.unsubscribeMetricAccount(accountNumber);
    });
  };

  subscribeTradeTopic = (topic) => {
    const { subscribeToTopic, client } = this.context;
    if (!topic || !client || !subscribeToTopic) {
      return;
    }

    subscribeToTopic(topic);
    this.subscribedTradeTopics.add(topic);
  };

  unsubscribeTradeTopic = (topic) => {
    const { unsubscribeFromTopic } = this.context;
    if (!topic || !unsubscribeFromTopic) {
      return;
    }

    unsubscribeFromTopic(topic);
    this.subscribedTradeTopics.delete(topic);
  };

  unsubscribeTradeTopics = (topics) => {
    topics.forEach((topic) => {
      this.unsubscribeTradeTopic(topic);
    });
  };

  getDesiredMetricAccounts = () => {
    const { activeAccountNumber } = this.state;

    if (!activeAccountNumber) {
      return [];
    }

    return [activeAccountNumber];
  };

  getDesiredTradeTopics = () => {
    const { activeAccountNumber } = this.state;
    const pairType = this.getPairTypeByAccountNumber(activeAccountNumber);
    const tradeSymbol = this.getTradeSymbol(pairType);

    if (!activeAccountNumber || !tradeSymbol) {
      return [];
    }

    return [`TradedataV2/${activeAccountNumber}/${tradeSymbol}`];
  };

  syncActiveMetricSubscription = () => {
    const { client } = this.context;
    if (!client) {
      return;
    }

    const desiredAccounts = new Set(this.getDesiredMetricAccounts());
    const nextSignature = [...desiredAccounts].sort().join("|");
    if (nextSignature !== this.metricSubscriptionSignature) {
      const currentAccounts = new Set(this.subscribedMetricAccounts);

      currentAccounts.forEach((accountNumber) => {
        if (!desiredAccounts.has(accountNumber)) {
          this.unsubscribeMetricAccount(accountNumber);
        }
      });

      desiredAccounts.forEach((accountNumber) => {
        if (!this.subscribedMetricAccounts.has(accountNumber)) {
          this.subscribeMetricAccount(accountNumber);
        }
      });

      this.metricSubscriptionSignature = nextSignature;
    }

    const desiredTradeTopics = new Set(this.getDesiredTradeTopics());
    const nextTradeSignature = [...desiredTradeTopics].sort().join("|");
    if (nextTradeSignature !== this.tradeSubscriptionSignature) {
      const currentTradeTopics = new Set(this.subscribedTradeTopics);

      currentTradeTopics.forEach((topic) => {
        if (!desiredTradeTopics.has(topic)) {
          this.unsubscribeTradeTopic(topic);
        }
      });

      desiredTradeTopics.forEach((topic) => {
        if (!this.subscribedTradeTopics.has(topic)) {
          this.subscribeTradeTopic(topic);
        }
      });

      this.tradeSubscriptionSignature = nextTradeSignature;
    }
  };

  getPairTypeByAccountNumber = (accountNumber) => {
    return this.state.pairTypes.find(
      (item) => item.accountNumber === String(accountNumber),
    );
  };

  getMetricDataForAccount = (accountNumber) => {
    const { messagesAccountMetric } = this.context;
    return (
      (messagesAccountMetric && messagesAccountMetric[accountNumber]) || null
    );
  };

  getProfitToday = (accountNumber) => {
    const { messagesAccountProfitToday } = this.context;
    const pairType = this.getPairTypeByAccountNumber(accountNumber);

    if (
      messagesAccountProfitToday &&
      messagesAccountProfitToday[accountNumber] &&
      typeof messagesAccountProfitToday[accountNumber].profit !== "undefined"
    ) {
      const rawProfit = Number(messagesAccountProfitToday[accountNumber].profit);
      const isCentAccount = pairType && pairType.isCent;
      return isCentAccount ? rawProfit / 100 : rawProfit;
    }

    return null;
  };

  getBaseName = (pairType) => {
    const rawBase = String((pairType && pairType.base) || "").toUpperCase();
    const rawKey = String((pairType && pairType.key) || "").toUpperCase();
    const source = rawBase || rawKey;

    if (source.indexOf("BTC") >= 0) {
      return "BTC";
    }
    if (source.indexOf("XAU") >= 0 || source.indexOf("GOLD") >= 0) {
      return "XAU";
    }
    if (source.indexOf("GBPJPY") >= 0 || source.indexOf("GBP") >= 0) {
      return "GBPJPY";
    }
    if (source.indexOf("EUR") >= 0) {
      return "EUR";
    }

    return source || "OTHER";
  };

  getBaseConfig = (base) => {
    return BASE_CONFIG[base] || BASE_CONFIG.OTHER;
  };

  getPairStyle = (pairType) => {
    const baseConfig = this.getBaseConfig(this.getBaseName(pairType));

    return {
      bgColor: (pairType && pairType.bgColor) || baseConfig.bg,
      textColor: (pairType && pairType.textColor) || baseConfig.color,
      borderColor: (pairType && pairType.borderColor) || baseConfig.border,
      iconColor:
        (pairType && pairType.iconColor) ||
        (pairType && pairType.textColor) ||
        baseConfig.color,
    };
  };

  getPairIcon = (pairType) => {
    const iconName = pairType && pairType.iconName;
    if (iconName && ICON_MAP[iconName]) {
      return ICON_MAP[iconName];
    }

    return this.getBaseConfig(this.getBaseName(pairType)).Icon;
  };

  getBaseOptions = () => {
    const counts = this.state.pairTypes.reduce((acc, pairType) => {
      const base = this.getBaseName(pairType);
      acc[base] = (acc[base] || 0) + 1;
      return acc;
    }, {});

    const order = ["EUR", "XAU", "BTC", "GBPJPY"];
    const orderedBases = order
      .filter((base) => counts[base])
      .concat(
        Object.keys(counts)
          .filter((base) => order.indexOf(base) < 0)
          .sort(),
      );

    return [
      { value: "ALL", label: "All", count: this.state.pairTypes.length },
      ...orderedBases.map((base) => ({
        value: base,
        label: this.getBaseConfig(base).label,
        count: counts[base],
      })),
    ];
  };

  getFilteredPairTypes = () => {
    const { activeBase, pairTypes } = this.state;
    if (activeBase === "ALL") {
      return pairTypes;
    }

    return pairTypes.filter(
      (pairType) => this.getBaseName(pairType) === activeBase,
    );
  };

  getTradeSymbol = (pairType) => {
    if (!pairType) {
      return null;
    }

    if (pairType.tradeSymbol) {
      return pairType.tradeSymbol;
    }

    const accountNumber = String(pairType.accountNumber);
    if (TRADE_SYMBOL_OVERRIDES[accountNumber]) {
      return TRADE_SYMBOL_OVERRIDES[accountNumber];
    }

    const base = this.getBaseName(pairType);
    const currency = String(pairType.currency || "").toLowerCase();

    if (base === "BTC") {
      return "BTCUSDc";
    }
    if (base === "XAU") {
      return currency === "standard" ? "XAUUSDm" : "XAUUSDc";
    }
    if (base === "GBPJPY") {
      return "GBPJPY";
    }
    if (base === "EUR") {
      return "EURUSDc";
    }

    return pairType.key || pairType.label || null;
  };

  getTradeData = (accountNumber) => {
    const { messagesTradeDataV2 } = this.context;
    const pairType = this.getPairTypeByAccountNumber(accountNumber);
    const mappedSymbol = this.getTradeSymbol(pairType);
    const key = `${accountNumber}_${mappedSymbol}`;

    if (messagesTradeDataV2 && messagesTradeDataV2[key]) {
      return {
        data: messagesTradeDataV2[key],
        symbol: mappedSymbol,
      };
    }

    const fallbackKey =
      messagesTradeDataV2 &&
      Object.keys(messagesTradeDataV2).find((item) =>
        item.startsWith(`${accountNumber}_`),
      );

    if (fallbackKey) {
      return {
        data: messagesTradeDataV2[fallbackKey],
        symbol: fallbackKey.replace(`${accountNumber}_`, ""),
      };
    }

    return {
      data: null,
      symbol: null,
    };
  };

  handleBaseChange = (event, newValue) => {
    const nextPairTypes =
      newValue === "ALL"
        ? this.state.pairTypes
        : this.state.pairTypes.filter(
            (pairType) => this.getBaseName(pairType) === newValue,
          );
    const activeStillVisible = nextPairTypes.some(
      (pairType) => pairType.accountNumber === this.state.activeAccountNumber,
    );

    this.setState({
      activeBase: newValue,
      activeAccountNumber: activeStillVisible
        ? this.state.activeAccountNumber
        : (nextPairTypes[0] && nextPairTypes[0].accountNumber) || "",
    });
  };

  handleTabChange = (event, newValue) => {
    this.setState({ activeAccountNumber: newValue });
  };

  formatMoney = (value) => {
    const numeric = Number(value || 0);
    const sign = numeric >= 0 ? "+" : "-";
    return `${sign}$${Math.abs(numeric).toFixed(2)}`;
  };

  getValueColor = (
    value,
    positive = "#079455",
    negative = "#d92d20",
    neutral = "#475467",
  ) => {
    const numeric = Number(value);
    if (numeric > 0) return positive;
    if (numeric < 0) return negative;
    return neutral;
  };

  renderMetricSkeleton() {
    return (
      <Box
        mt={1.5}
        sx={{
          bgcolor: "#ffffff",
          border: "1px solid #d8e3ef",
          borderRadius: 2,
          p: { xs: 0.8, sm: 1 },
        }}
      >
        <Skeleton height={22} />
        <Grid container spacing={0.75} mt={0.25}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={6} md={3} key={item}>
              <Skeleton height={42} sx={{ borderRadius: 1 }} />
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  }

  renderOrderPanel = (title, order, accentColor, estimatedProfit) => {
    const totalLots = Number(order.totalLots || 0);
    const pointDiff = Number(order.pointDiff || 0);
    const details = [
      { label: "TP", value: order.takeProfit || "-", color: "#38bdf8" },
      {
        label: "Point",
        value: pointDiff,
        color: this.getValueColor(pointDiff, "#f59e0b", "#ef4444"),
      },
      {
        label: "Est",
        value: `$${Number(estimatedProfit || 0).toFixed(2)}`,
        color: this.getValueColor(estimatedProfit),
      },
      {
        label: "Lot",
        value: totalLots.toFixed(2),
        color: this.getValueColor(totalLots, "#f59e0b", "#ef4444"),
      },
    ];

    return (
      <Box
        sx={{
          bgcolor: "#ffffff",
          border: `1px solid ${accentColor}2e`,
          borderRadius: 1.5,
          p: { xs: 0.75, sm: 0.85, lg: 1, xl: 1.1 },
          height: "100%",
          fontFamily: '"Roboto", "Arial", sans-serif',
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 0.75,
            mb: 0.55,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.55 }}>
            {title === "Buy" ? (
              <TrendingUp sx={{ color: accentColor, fontSize: RESPONSIVE_TEXT.icon }} />
            ) : (
              <TrendingDown sx={{ color: accentColor, fontSize: RESPONSIVE_TEXT.icon }} />
            )}
            <Typography
              sx={{
                fontWeight: 700,
                color: accentColor,
                fontSize: RESPONSIVE_TEXT.detailTitle,
                lineHeight: 1.15,
              }}
            >
              {title}
            </Typography>
          </Box>
          <Chip
            size="small"
            label={`${order.totalOrder || 0} order`}
            sx={{
              height: 20,
              bgcolor: `${accentColor}14`,
              color: accentColor,
              border: `1px solid ${accentColor}33`,
              fontWeight: 700,
              fontSize: RESPONSIVE_TEXT.chip,
              "& .MuiChip-label": { px: 0.65 },
            }}
          />
        </Box>

        <Grid container spacing={0.65}>
          {details.map((item) => (
            <Grid item xs={6} sm={3} key={item.label}>
              <Box
                sx={{
                  bgcolor: "#f6f9fc",
                  border: "1px solid #e6edf5",
                  color: "#1d2939",
                  borderRadius: 1,
                  px: { xs: 0.7, lg: 0.85, xl: 0.95 },
                  py: { xs: 0.42, lg: 0.55, xl: 0.65 },
                }}
              >
                <Typography
                  sx={{
                    color: "#667085",
                    fontSize: RESPONSIVE_TEXT.label,
                    fontWeight: 600,
                  }}
                >
                  {item.label}
                </Typography>
                <Typography
                  sx={{
                    color: item.color,
                    fontSize: RESPONSIVE_TEXT.detailValue,
                    fontWeight: 700,
                    mt: 0.1,
                    overflowWrap: "anywhere",
                  }}
                >
                  {item.value}
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>
    );
  };

  renderMetric(data, profitToday, pairType) {
    if (!data) {
      return this.renderMetricSkeleton();
    }

    const accountNumber = String(
      (pairType && pairType.accountNumber) || data.accountNumber,
    );
    const isCentAccount = pairType && pairType.isCent;
    const converted = {
      ...data,
      balance: Number(data.balance || 0),
      equity: Number(data.equity || 0),
      floating: Number(data.floating || 0),
    };

    if (isCentAccount) {
      converted.balance = converted.balance / 100;
      converted.equity = converted.equity / 100;
      converted.floating = converted.floating / 100;
    }

    const standardCapital = pairType && Number(pairType.capital);
    const floatPercent = standardCapital
      ? ((converted.floating / standardCapital) * 100).toFixed(2)
      : "0.00";
    const groupOrders = (data.order && data.order.tpGroups) || [];
    const groupOrdersBuy = groupOrders.find((o) => o.type === 0) || {};
    const groupOrdersSell = groupOrders.find((o) => o.type === 1) || {};
    const estProfitBuy = isCentAccount
      ? Number(groupOrdersBuy.estimatedProfit || 0) / 100
      : Number(groupOrdersBuy.estimatedProfit || 0);
    const estProfitSell = isCentAccount
      ? Number(groupOrdersSell.estimatedProfit || 0) / 100
      : Number(groupOrdersSell.estimatedProfit || 0);
    const totalLots =
      Number(groupOrdersBuy.totalLots || 0) +
      Number(groupOrdersSell.totalLots || 0);
    const { symbol: tradeSymbol } = this.getTradeData(accountNumber);
    const priceList = data && data.price ? data.price : {};
    let symbolKey = tradeSymbol;

    if (!symbolKey && Object.keys(priceList).length > 0) {
      symbolKey = Object.keys(priceList)[0];
    }

    const priceInfo = priceList[symbolKey];
    const bid = priceInfo && priceInfo.bid != null ? priceInfo.bid : null;
    const ask = priceInfo && priceInfo.ask != null ? priceInfo.ask : null;
    const priceBuy = bid;
    const priceSell = ask;
    let spreadPoint = null;

    if (bid != null && ask != null && priceInfo) {
      const bidNumber = Number(bid);
      const askNumber = Number(ask);
      spreadPoint = Math.round(
        (askNumber - bidNumber) / Number(priceInfo.point || 1),
      );

      if (["EURUSD", "EURUSDc"].includes(symbolKey)) {
        spreadPoint = Math.round((askNumber - bidNumber) * 100000);
      } else if (["XAUUSD", "XAUUSDc", "XAUUSDm"].includes(symbolKey)) {
        spreadPoint = Math.round((askNumber - bidNumber) * 100);
      } else if (["BTCUSD", "BTCUSDc", "BTCUSDm"].includes(symbolKey)) {
        spreadPoint = Math.round(askNumber - bidNumber);
      } else if (["GBPJPY", "GBPJPYc", "GBPJPYm"].includes(symbolKey)) {
        spreadPoint = Math.round((askNumber - bidNumber) * 100);
      } else {
        spreadPoint = Math.round(askNumber - bidNumber);
      }
    }

    const activeLabel = (pairType && pairType.label) || accountNumber;
    const baseName = this.getBaseName(pairType);
    const pairStyle = this.getPairStyle(pairType);
    const HeaderIcon = this.getPairIcon(pairType);
    const headerChips = [
      {
        label: baseName,
        color: pairStyle.textColor,
        bg: pairStyle.bgColor,
        border: pairStyle.borderColor,
      },
      {
        label: (pairType && pairType.currency) || "-",
        color: "#92400e",
        bg: "#fff7d6",
        border: "rgba(251,191,36,0.35)",
      },
      {
        label: standardCapital ? `$${standardCapital}` : "-",
        color: "#047857",
        bg: "#e8f7ef",
        border: "rgba(52,211,153,0.35)",
      },
    ];
    const metricItems = [
      {
        label: "Floating",
        value: this.formatMoney(converted.floating),
        color: this.getValueColor(converted.floating),
      },
      {
        label: "Profit Today",
        value:
          profitToday != null && !isNaN(parseFloat(profitToday))
            ? this.formatMoney(profitToday)
            : "-",
        color: this.getValueColor(profitToday || 0),
      },
      {
        label: "Float %",
        value: `${floatPercent}%`,
        color: this.getValueColor(floatPercent),
      },
      {
        label: "Total Lot",
        value: totalLots.toFixed(2),
        color: this.getValueColor(totalLots, "#f59e0b", "#ef4444"),
      },
    ];

    return (
      <Box
        mt={0.65}
        sx={{
          bgcolor: "#ffffff",
          borderRadius: 2,
          border: "1px solid #d8e3ef",
          boxShadow: "0 8px 18px rgba(15,23,42,0.08)",
          color: "#1d2939",
          overflow: "hidden",
          fontFamily: '"Roboto", "Arial", sans-serif',
        }}
      >
        <Box
          sx={{
            px: { xs: 1, sm: 1.2, lg: 1.45, xl: 1.7 },
            py: { xs: 0.65, sm: 0.75, lg: 0.9, xl: 1 },
            background:
              "linear-gradient(135deg, #f8fbff, #eef5fc)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 0.65,
            borderBottom: "1px solid #dbe7f3",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, minWidth: 0 }}>
            <Box
              sx={{
                width: { xs: 22, md: 24, lg: 26, xl: 28 },
                height: { xs: 22, md: 24, lg: 26, xl: 28 },
                borderRadius: 1.5,
                display: "grid",
                placeItems: "center",
                bgcolor: pairStyle.bgColor,
                color: pairStyle.iconColor,
                border: `1px solid ${pairStyle.borderColor}`,
                flex: "0 0 auto",
              }}
            >
              <HeaderIcon sx={{ fontSize: RESPONSIVE_TEXT.icon }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: RESPONSIVE_TEXT.title,
                  fontWeight: 750,
                  lineHeight: 1.1,
                  overflowWrap: "anywhere",
                }}
              >
                {activeLabel}
              </Typography>
              <Typography
                sx={{
                  color: "#667085",
                  fontSize: RESPONSIVE_TEXT.meta,
                  mt: 0.1,
                }}
              >
                {accountNumber}
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.45 }}>
            {headerChips.map((chip) => (
              <Chip
                key={chip.label}
                size="small"
                label={chip.label}
                sx={{
                  bgcolor: chip.bg,
                  color: chip.color,
                  border: `1px solid ${chip.border || "rgba(15,23,42,0.08)"}`,
                  fontWeight: 700,
                  height: { xs: 19, md: 21, lg: 22, xl: 23 },
                  fontSize: RESPONSIVE_TEXT.chip,
                  "& .MuiChip-label": { px: 0.65 },
                }}
              />
            ))}
          </Box>
        </Box>

        <Box
          sx={{
            px: { xs: 0.85, sm: 1, lg: 1.2, xl: 1.4 },
            py: { xs: 0.65, sm: 0.75, lg: 0.9, xl: 1 },
          }}
        >
          <Grid container spacing={0.75}>
            {metricItems.map((item) => (
              <Grid item xs={6} md={3} key={item.label}>
                <Box
                  sx={{
                    bgcolor: "#f6f9fc",
                    border: "1px solid #e4ebf4",
                    borderRadius: 1.5,
                    px: { xs: 0.75, sm: 0.85 },
                    py: { xs: 0.5, sm: 0.55, lg: 0.7, xl: 0.8 },
                    minHeight: { xs: 40, md: 44, lg: 50, xl: 54 },
                  }}
                >
                  <Typography
                    sx={{
                      color: "#667085",
                      fontSize: RESPONSIVE_TEXT.label,
                      fontWeight: 600,
                      textTransform: "uppercase",
                    }}
                  >
                    {item.label}
                  </Typography>
                  <Typography
                    sx={{
                      color: item.color,
                      fontSize: RESPONSIVE_TEXT.value,
                      fontWeight: 750,
                      lineHeight: 1.2,
                      mt: 0.08,
                      overflowWrap: "anywhere",
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          {priceBuy && priceSell && (
            <Box
              sx={{
                mt: 0.55,
                bgcolor: "#eff8ff",
                border: "1px solid #d1e9ff",
                borderRadius: 1.5,
                px: { xs: 0.75, sm: 0.85, lg: 1, xl: 1.1 },
                py: { xs: 0.42, lg: 0.55, xl: 0.65 },
                display: "flex",
                justifyContent: "space-between",
                flexWrap: "wrap",
                gap: 0.75,
              }}
            >
              <Typography sx={{ color: "#175cd3", fontSize: RESPONSIVE_TEXT.price }}>
                BID/ASK:{" "}
                <strong>
                  {priceBuy || "-"} / {priceSell || "-"}
                </strong>
              </Typography>
              <Typography sx={{ color: "#175cd3", fontSize: RESPONSIVE_TEXT.price }}>
                Spread:{" "}
                <strong>{spreadPoint != null ? spreadPoint : "-"} pts</strong>
              </Typography>
            </Box>
          )}

          <Divider sx={{ bgcolor: "#e6edf5", my: 0.65 }} />

          <Grid container spacing={0.75}>
            <Grid item xs={12} md={6}>
              {this.renderOrderPanel(
                "Buy",
                groupOrdersBuy,
                "#22c55e",
                estProfitBuy,
              )}
            </Grid>
            <Grid item xs={12} md={6}>
              {this.renderOrderPanel(
                "Sell",
                groupOrdersSell,
                "#ef4444",
                estProfitSell,
              )}
            </Grid>
          </Grid>
        </Box>
      </Box>
    );
  }

  render() {
    const {
      activeAccountNumber,
      pairTypes,
      isLoadingPairTypes,
      pairTypesError,
      activeBase,
    } = this.state;
    const activePairType = this.getPairTypeByAccountNumber(activeAccountNumber);
    const profitToday = this.getProfitToday(activeAccountNumber);
    const metricData = this.getMetricDataForAccount(activeAccountNumber);
    const baseOptions = this.getBaseOptions();
    const filteredPairTypes = this.getFilteredPairTypes();

    if (isLoadingPairTypes) {
      return (
        <Box
          sx={{
            bgcolor: "#f7f9fc",
            border: "1px solid #dce3ee",
            borderRadius: 2,
            p: { xs: 0.75, sm: 0.9, lg: 1.05, xl: 1.2 },
          }}
        >
          <Skeleton height={36} />
          <Skeleton height={32} sx={{ mt: 0.6 }} />
          {this.renderMetricSkeleton()}
        </Box>
      );
    }

    if (pairTypesError || pairTypes.length === 0) {
      return (
        <Box
          mt={2}
          sx={{
            bgcolor: "#1e2f4d",
            borderRadius: 2,
            px: { xs: 1.5, sm: 2, md: 3 },
            py: { xs: 1.5, sm: 2 },
            color: "#fff",
          }}
        >
          <Typography sx={{ fontSize: RESPONSIVE_TEXT.title }}>
            {pairTypesError || "Pair type data belum tersedia."}
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          bgcolor: "#f7f9fc",
          border: "1px solid #dce3ee",
          borderRadius: 2,
          p: { xs: 0.75, sm: 0.9, lg: 1.05, xl: 1.2 },
          fontFamily: '"Roboto", "Arial", sans-serif',
        }}
      >
        <Tabs
          value={activeBase}
          onChange={this.handleBaseChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            bgcolor: "#fff",
            border: "1px solid #e5eaf2",
            borderRadius: 2,
            minHeight: { xs: 32, md: 36, lg: 40, xl: 44 },
            px: 0.45,
            mb: 0.5,
            "& .MuiTabs-flexContainer": { gap: 0.45 },
            "& .MuiTab-root": {
              textTransform: "none",
              minHeight: { xs: 25, md: 29, lg: 32, xl: 35 },
              borderRadius: 1.5,
              color: "#42526e",
              fontFamily: '"Roboto", "Arial", sans-serif',
              fontWeight: 700,
              px: { xs: 0.7, sm: 0.95, md: 1.1, lg: 1.25, xl: 1.4 },
              my: 0.35,
              fontSize: RESPONSIVE_TEXT.baseTab,
            },
            "& .Mui-selected": {
              color: "inherit",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {baseOptions.map((base) => {
            const config = this.getBaseConfig(base.value);
            const Icon = config.Icon;
            const selected = activeBase === base.value;

            return (
              <Tab
                key={base.value}
                value={base.value}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <Icon
                      sx={{
                        color: selected ? config.color : "#667085",
                        fontSize: RESPONSIVE_TEXT.icon,
                      }}
                    />
                    <span>{base.label}</span>
                    <Chip
                      size="small"
                      label={base.count}
                      sx={{
                        height: { xs: 17, md: 19, lg: 21, xl: 22 },
                        minWidth: { xs: 22, md: 24, lg: 26, xl: 28 },
                        bgcolor: selected ? config.color : config.bg,
                        color: selected ? "#fff" : config.color,
                        border: `1px solid ${
                          selected ? "rgba(255,255,255,0.55)" : config.border
                        }`,
                        boxShadow: selected
                          ? "0 2px 5px rgba(15,23,42,0.18)"
                          : "none",
                        fontWeight: 800,
                        fontSize: RESPONSIVE_TEXT.chip,
                        lineHeight: 1,
                        "& .MuiChip-label": {
                          px: 0.55,
                          minWidth: 12,
                          textAlign: "center",
                        },
                      }}
                    />
                  </Box>
                }
                sx={{
                  bgcolor: selected ? config.bg : "transparent",
                  color: selected ? `${config.color} !important` : "#42526e",
                  border: selected
                    ? `1px solid ${config.border}`
                    : "1px solid transparent",
                  boxShadow: selected
                    ? "0 4px 10px rgba(15,23,42,0.08)"
                    : "none",
                  "&.Mui-selected": {
                    bgcolor: config.bg,
                    color: `${config.color} !important`,
                  },
                }}
              />
            );
          })}
        </Tabs>

        <Tabs
          value={activeAccountNumber}
          onChange={this.handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            mb: 0.15,
            borderRadius: 2,
            bgcolor: "#e8edf5",
            px: 0.45,
            minHeight: { xs: 30, md: 34, lg: 38, xl: 42 },
            "& .MuiTabs-flexContainer": { gap: 0.45 },
            "& .MuiTab-root": {
              textTransform: "none",
              fontFamily: '"Roboto", "Arial", sans-serif',
              fontWeight: 700,
              borderRadius: 1.5,
              minHeight: { xs: 24, md: 28, lg: 31, xl: 34 },
              maxWidth: { xs: 140, sm: 190, lg: 220, xl: 260 },
              color: "#334155",
              fontSize: RESPONSIVE_TEXT.pairTab,
              px: { xs: 0.8, sm: 1.1, md: 1.25, lg: 1.4, xl: 1.55 },
              my: 0.35,
              overflow: "hidden",
            },
            "& .Mui-selected": {
              color: "inherit",
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {filteredPairTypes.map((pairType) => {
            const pairStyle = this.getPairStyle(pairType);
            const PairIcon = this.getPairIcon(pairType);
            const selected = pairType.accountNumber === activeAccountNumber;

            return (
              <Tab
                key={pairType.accountNumber}
                value={pairType.accountNumber}
                label={
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
                    <PairIcon
                      sx={{
                        color: selected ? pairStyle.iconColor : "#667085",
                        fontSize: RESPONSIVE_TEXT.icon,
                      }}
                    />
                    <span>{pairType.label}</span>
                  </Box>
                }
                sx={{
                  bgcolor: selected ? pairStyle.bgColor : "transparent",
                  color: selected
                    ? `${pairStyle.textColor} !important`
                    : "#334155",
                  border: selected
                    ? `1px solid ${pairStyle.borderColor}`
                    : "1px solid transparent",
                  boxShadow: selected
                    ? "0 4px 10px rgba(15,23,42,0.08)"
                    : "none",
                  "&.Mui-selected": {
                    bgcolor: pairStyle.bgColor,
                    color: `${pairStyle.textColor} !important`,
                  },
                }}
              />
            );
          })}
        </Tabs>

        <Box p={0} mb={0.25}>
          {this.renderMetric(metricData, profitToday, activePairType)}
        </Box>
      </Box>
    );
  }
}

export default AccountMetricTabs;
